import React from 'react';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Link,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const dataWeCollect: { type: string; examples: string; purpose: string }[] = [
  {
    type: 'Personal information',
    examples: 'Name, email address, and account identifiers.',
    purpose: 'Account creation, authentication, and support.',
  },
  {
    type: 'Chess & app content',
    examples:
      'Chess games and moves (PGNs), imported games, puzzle and training progress, notes, favorites, and settings.',
    purpose: 'Providing game analysis, training, and personalized insights.',
  },
  {
    type: 'Photos',
    examples:
      'Images you capture or upload to scan a chess position, and profile pictures.',
    purpose: 'Converting board images to positions and displaying your profile.',
  },
  {
    type: 'Purchase information',
    examples:
      'Subscription status and purchase history. Card details are entered directly into our payment processor and are never stored on our servers.',
    purpose: 'Processing and managing subscriptions.',
  },
  {
    type: 'App activity & diagnostics',
    examples:
      'In-app interactions, crash logs, and performance diagnostics.',
    purpose: 'Understanding usage, fixing bugs, and improving the app.',
  },
  {
    type: 'Device & other identifiers',
    examples:
      'Device identifiers and push notification tokens.',
    purpose: 'Delivering notifications and app functionality.',
  },
];

const thirdParties: { name: string; role: string }[] = [
  {
    name: 'Auth0 (Okta)',
    role: 'Authentication and account sign-in.',
  },
  {
    name: 'Google Firebase (Cloud Messaging & Analytics)',
    role: 'Push notifications and usage analytics.',
  },
  {
    name: 'Sentry',
    role: 'Crash reporting and performance diagnostics.',
  },
  {
    name: 'Stripe',
    role: 'Payment processing and subscription billing.',
  },
  {
    name: 'Lichess',
    role: 'Optional import of your games when you connect your Lichess account.',
  },
  {
    name: 'Amazon Web Services (AWS)',
    role: 'Cloud hosting, storage, and infrastructure.',
  },
  {
    name: 'AI providers (OpenAI, Google)',
    role: 'Generating chess analysis and coaching insights from game data.',
  },
];

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Privacy Policy
          </Typography>
          <Link component={RouterLink} to="/support" color="inherit" sx={{ mr: 2 }}>
            Support
          </Link>
          <Link component={RouterLink} to="/terms" color="inherit">
            Terms of Service
          </Link>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>
        <Typography variant="body1" paragraph>
          This Privacy Policy explains what information ChessWiz ("we", "us")
          collects, how we use it, and who we share it with when you use our
          mobile app and websites. We only collect data that is needed to run and
          improve ChessWiz, and we are committed to keeping it secure.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          1. Information We Collect
        </Typography>
        <Typography variant="body1" paragraph>
          Depending on how you use ChessWiz, we may collect the following
          categories of data:
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small" aria-label="Data we collect">
            <TableHead>
              <TableRow>
                <TableCell><strong>Data type</strong></TableCell>
                <TableCell><strong>Examples</strong></TableCell>
                <TableCell><strong>Why we collect it</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataWeCollect.map((row) => (
                <TableRow key={row.type}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.examples}</TableCell>
                  <TableCell>{row.purpose}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect to: create and secure your account;
          provide chess game analysis, puzzles, and training; generate
          AI-powered insights and coaching; process subscriptions; send
          notifications you have enabled; provide customer support; and monitor,
          troubleshoot, and improve the app's reliability and performance.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          3. How Your Information Is Shared
        </Typography>
        <Typography variant="body1" paragraph>
          We do not sell your personal information. We share data only with
          service providers who process it on our behalf to operate ChessWiz, and
          only as needed for the purposes described above. These providers
          include:
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small" aria-label="Third-party service providers">
            <TableHead>
              <TableRow>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>How they help</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {thirdParties.map((row) => (
                <TableRow key={row.name}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.role}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body1" paragraph>
          We may also disclose information if required by law or to protect the
          rights, safety, and security of ChessWiz and its users. If ChessWiz is
          involved in a merger or acquisition, data may be transferred as part of
          that transaction.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          4. Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          All data transmitted between the app and our servers is encrypted in
          transit using industry-standard TLS/HTTPS. We implement appropriate
          technical and organizational measures to protect your information
          against unauthorized access, alteration, disclosure, or destruction.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          5. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your information for as long as your account is active or as
          needed to provide the service. When you request deletion, your account
          is deactivated immediately and permanently removed after a short grace
          period, subject to any legal retention obligations.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          6. Your Rights & Deleting Your Data
        </Typography>
        <Typography variant="body1" paragraph>
          You may access, correct, or delete your personal data at any time. You
          can delete your account and associated data directly from within the app
          (Settings → Delete account) or via our{' '}
          <Link component={RouterLink} to="/delete-account">
            account deletion page
          </Link>
          . You may also contact us to exercise any privacy rights available to
          you under applicable law.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          7. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          ChessWiz is not directed to children under the age of 13, and we do not
          knowingly collect personal information from them. If you believe a child
          has provided us with personal data, please contact us so we can remove
          it.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          8. International Data Transfers
        </Typography>
        <Typography variant="body1" paragraph>
          Your information may be processed and stored on servers located outside
          your country of residence, including in the United States. We take steps
          to ensure your data is handled in accordance with this policy wherever
          it is processed.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          9. Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. Material changes
          will be reflected by updating the "Last updated" date above, and where
          appropriate we will provide additional notice.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
          10. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about this Privacy Policy or how we handle
          your data, please contact us at privacy@chesswiz.net.
        </Typography>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
